import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import WorkIcon from '@mui/icons-material/Work';
import TodayIcon from '@mui/icons-material/Today';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { usePositionDialogContent } from './ActionDialog/usePositionDialogContent';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
    useUpdatePositionsMappedToChangeRequestPolicyMutation,
    useUpdatePositionsMappedToRecruitmentPolicyMutation,
    useResyncPositionMutation,
    useScheduleIntakeMutation,
    useCompleteIntakeMutation,
    useModifyPositionMutation,
    useUpdatePositionStartDateMutation,
    useUpdateSinglePositionStatusMutation,
    useBulkUpdatePositionStatusMutation,
} from '../../../api/positions';
import { usePositionStatus } from '../../../hooks/use-position-status';
import { updateAllSelectedPositions } from '../../../store/slices/positions';

export const allowedPositionActions = (type, actionType, position, data) => {
    if (type === 'bulk') {
        if (data?.statuses?.length === 1) {
            const status = data?.statuses?.[0];
            if ((actionType === 'open-intake' || actionType === 'reject') && status === 'Draft') return true;
            if (actionType === 'schedule-intake' && status === 'Open Pending Intake') return true;
            if (actionType === 'reschedule-intake' && status === 'Intake Scheduled') return true;
            if (actionType === 'complete-intake' && status === 'Intake Scheduled') return true;
        }

        if (
            actionType === 'map-cr-policy' &&
            data?.statuses?.every((status) => ['Offer Released', 'Offer Accepted', 'Filled'].indexOf(status) < 0)
        )
            return true;
        if (
            actionType === 'resync' &&
            data?.statuses?.every(
                (status) => ['Draft', 'Open Pending Intake', 'Intake Scheduled'].indexOf(status) < 0,
            ) &&
            data?.user?.isIntegratedWithCanvas
        )
            return true;
        if (
            actionType === 'create-cr' &&
            data?.statuses?.every(
                (status) => ['Draft', 'Offer Released', 'Offer Accepted', 'Filled'].indexOf(status) < 0,
            )
        )
            return true;
        return false;
    } else {
        if ((actionType === 'open-intake' || actionType === 'reject') && position?.['status'] === 'Draft') return true;
        if (actionType === 'modify' && position?.['status'] === 'Draft') return true;
        if (
            actionType === 'map-cr-policy' &&
            ['Offer Released', 'Offer Accepted', 'Filled'].indexOf(position?.['status']) < 0
        )
            return true;
        if (actionType === 'map-recruitment-policy') return true;
        if (
            actionType === 'create-cr' &&
            position?.['changeRequestPolicy'] &&
            ['Draft', 'Offer Released', 'Offer Accepted', 'Filled'].indexOf(position?.['status']) < 0
        )
            return true;
        if (actionType === 'change-status' && position?.['status'] !== 'Filled' && !data?.user?.isIntegratedWithCanvas)
            return true;
        if (actionType === 'schedule-intake' && position?.['status'] === 'Open Pending Intake') return true;
        if (actionType === 'reschedule-intake' && position?.['status'] === 'Intake Scheduled') return true;
        if (actionType === 'complete-intake' && position?.['status'] === 'Intake Scheduled') return true;
        if (
            actionType === 'resync' &&
            ['Draft', 'Open Pending Intake', 'Intake Scheduled'].indexOf(position?.['status']) < 0 &&
            data?.user?.isIntegratedWithCanvas
        )
            return true;
        if (
            actionType === 'update-start-date' &&
            ['Open Active Sourcing', 'On Hold'].indexOf(position?.['status']) >= 0
        )
            return true;
        return false;
    }
};

const PositionMenu = ({ Component, row, handleAction, type = 'single', label }) => {
    const [actionType, setActionType] = useState();
    const [modalType, setModalType] = useState(1);
    const { selectedPositions } = useSelector((state) => state.positions);
    const { user } = useSelector((state) => state.session);
    const [positionStatuses] = usePositionStatus('positions');
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [updatePositionsMappedToChangeRequestPolicy] = useUpdatePositionsMappedToChangeRequestPolicyMutation();
    const [updatePositionsMappedToRecruitmentPolicy] = useUpdatePositionsMappedToRecruitmentPolicyMutation();
    const [resyncPosition] = useResyncPositionMutation();
    const [scheduleIntake] = useScheduleIntakeMutation();
    const [completeIntake] = useCompleteIntakeMutation();
    const [modifyPosition] = useModifyPositionMutation();
    const [updatePositionStartDate] = useUpdatePositionStartDateMutation();
    const [updateSinglePositionStatus] = useUpdateSinglePositionStatusMutation();
    const [bulkUpdatePositionStatus] = useBulkUpdatePositionStatusMutation();

    const handleConfirm = async (payload) => {
        try {
            setActionType();
            const data = new FormData();
            const selectedPositionIds = type === 'bulk' ? selectedPositions : [row?.id];

            if (actionType === 'map-cr-policy') {
                data.append('positionIds', selectedPositionIds.join(','));
                data.append('changeRequestPolicyId', payload.crPolicy);

                const response = await updatePositionsMappedToChangeRequestPolicy(data).unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                enqueueSnackbar(response?.message, { variant: 'success' });
                handleAction();
            } else if (actionType === 'resync') {
                data.append('positionIds', selectedPositionIds.join(','));

                const response = await resyncPosition(data).unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                enqueueSnackbar(response?.message, { variant: 'success' });
                handleAction();
            } else if (actionType === 'schedule-intake' || actionType === 'reschedule-intake') {
                data.append('positionIds', selectedPositionIds.join(','));
                for (const key in payload) data.append(key, payload[key]);

                const response = await scheduleIntake(data).unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                enqueueSnackbar(response?.message, { variant: 'success' });
                handleAction();
            } else if (actionType === 'complete-intake') {
                data.append('positionIds', selectedPositionIds.join(','));

                const response = await completeIntake(data).unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                enqueueSnackbar(response?.message, { variant: 'success' });
                handleAction();
            } else if (actionType === 'change-status') {
                data.append('id', selectedPositionIds.join(','));
                for (const key in payload) {
                    data.append(key, payload[key]);
                }

                const response = await modifyPosition(data).unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                enqueueSnackbar('Position status updated successfully.', { variant: 'success' });
                handleAction();
            } else if (actionType === 'update-start-date') {
                data.append('positionId', selectedPositionIds.join(','));
                data.append('startDate', payload.startDate);

                const response = await updatePositionStartDate(data).unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                enqueueSnackbar(response?.message, { variant: 'success' });
                handleAction();
            } else if (actionType === 'open-intake' || actionType === 'reject') {
                if (type === 'bulk') data.append('positionIds', selectedPositionIds.join(','));
                else data.append('positionId', selectedPositionIds.join(','));
                for (const key in payload) data.append(key, payload[key]);
                const response =
                    type === 'bulk'
                        ? await bulkUpdatePositionStatus(data).unwrap()
                        : await updateSinglePositionStatus(data).unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                enqueueSnackbar(response?.message, { variant: 'success' });
                handleAction();
            } else if (actionType === 'map-recruitment-policy') {
                data.append('positionIds', selectedPositionIds.join(','));
                data.append('recruitmentPolicyId', payload.recruitmentPolicy);

                const response = await updatePositionsMappedToRecruitmentPolicy(data).unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                enqueueSnackbar(response?.message, { variant: 'success' });
                handleAction();
            }
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Something went wrong', { variant: 'error' });
        } finally {
            if (type === 'bulk')
                dispatch(
                    updateAllSelectedPositions({
                        isChecked: false,
                    }),
                );
        }
    };

    const dialogContent = usePositionDialogContent({
        data: row,
        actionType,
        setActionType,
        modalType,
        setModalType,
        handleConfirm,
        type,
    });

    const actionItems =
        type === 'bulk'
            ? [
                  {
                      label: 'Open Intake',
                      type: 'open-intake',
                      icon: <CheckIcon />,
                      onClick: (action) => setActionType(action.type),
                      permissions: ['ROLE_BASE_PERMISSION_RECRUIT_INDEX_BULK_UPDATE_POSITION_STATUS'],
                  },
                  {
                      label: 'Reject',
                      type: 'reject',
                      icon: <CloseIcon />,
                      onClick: (action) => setActionType(action.type),
                      permissions: ['ROLE_BASE_PERMISSION_RECRUIT_INDEX_BULK_UPDATE_POSITION_STATUS'],
                  },
                  {
                      label: 'Map Change Policy',
                      type: 'map-cr-policy',
                      icon: <InsertDriveFileIcon />,
                      onClick: (action) => setActionType(action.type),
                      permissions: ['ROLE_BASE_PERMISSION_RECRUIT_INDEX_MAP_POSITION_TO_CHANGE_REQUEST_POLICY'],
                  },
                  {
                      label: 'Schedule Intake',
                      type: 'schedule-intake',
                      icon: <TodayIcon />,
                      onClick: (action) => setActionType(action.type),
                      permissions: ['ROLE_BASE_PERMISSION_RECRUIT_INDEX_SCHEDULE_INTAKE'],
                  },
                  {
                      label: 'Reschedule Intake',
                      type: 'reschedule-intake',
                      icon: <EventRepeatIcon />,
                      onClick: (action) => setActionType(action.type),
                      permissions: ['ROLE_BASE_PERMISSION_RECRUIT_INDEX_SCHEDULE_INTAKE'],
                  },
                  {
                      label: 'Complete Intake',
                      type: 'complete-intake',
                      icon: <EventAvailableIcon />,
                      onClick: (action) => setActionType(action.type),
                      permissions: ['ROLE_BASE_PERMISSION_RECRUIT_INDEX_COMPLETE_INTAKE'],
                  },
                  {
                      label: 'Resync',
                      type: 'resync',
                      icon: <AutorenewIcon />,
                      onClick: (action) => setActionType(action.type),
                      permissions: ['ROLE_BASE_PERMISSION_RECRUIT_INDEX_RESYNC_POSITION'],
                  },
                  {
                      label: 'Create Change Request',
                      type: 'create-cr',
                      icon: <ChangeCircleIcon />,
                      onClick: (action) => setActionType(action.type),
                      permissions: ['ROLE_BASE_PERMISSION_RECRUIT_INDEX_CREATE_CHANGE_REQUEST_BULK'],
                  },
              ]
            : [
                  {
                      label: 'Open Intake',
                      type: 'open-intake',
                      icon: <CheckIcon />,
                      onClick: (action) => setActionType(action.type),
                      permissions: ['ROLE_BASE_PERMISSION_RECRUIT_INDEX_UPDATE_SINGLE_POSITION_STATUS'],
                  },
                  {
                      label: 'Modify',
                      type: 'modify',
                      icon: <EditIcon />,
                      onClick: () => navigate(`/positions/modify/${row.id}`),
                      permissions: ['ROLE_BASE_PERMISSION_RECRUIT_INDEX_UPDATE_POSITION'],
                  },
                  {
                      label: 'Reject',
                      type: 'reject',
                      icon: <CloseIcon />,
                      onClick: (action) => setActionType(action.type),
                      permissions: ['ROLE_BASE_PERMISSION_RECRUIT_INDEX_UPDATE_SINGLE_POSITION_STATUS'],
                  },
                  {
                      label: 'Map Change Policy',
                      type: 'map-cr-policy',
                      icon: <InsertDriveFileIcon />,
                      onClick: (action) => setActionType(action.type),
                      permissions: ['ROLE_BASE_PERMISSION_RECRUIT_INDEX_MAP_POSITION_TO_CHANGE_REQUEST_POLICY'],
                  },
                  {
                      label: 'Create Change Request',
                      type: 'create-cr',
                      icon: <ChangeCircleIcon />,
                      onClick: (action) => setActionType(action.type),
                      permissions: ['ROLE_BASE_PERMISSION_RECRUIT_INDEX_CREATE_CHANGE_REQUEST'],
                  },
                  {
                      label: 'Change Status',
                      type: 'change-status',
                      icon: <PublishedWithChangesIcon />,
                      onClick: (action) => setActionType(action.type),
                      permissions: ['ROLE_BASE_PERMISSION_RECRUIT_INDEX_UPDATE_POSITION_STATUS'],
                  },
                  {
                      label: 'Schedule Intake',
                      type: 'schedule-intake',
                      icon: <TodayIcon />,
                      onClick: (action) => setActionType(action.type),
                      permissions: ['ROLE_BASE_PERMISSION_RECRUIT_INDEX_SCHEDULE_INTAKE'],
                  },
                  {
                      label: 'Reschedule Intake',
                      type: 'reschedule-intake',
                      icon: <EventRepeatIcon />,
                      onClick: (action) => setActionType(action.type),
                      permissions: ['ROLE_BASE_PERMISSION_RECRUIT_INDEX_SCHEDULE_INTAKE'],
                  },
                  {
                      label: 'Complete Intake',
                      type: 'complete-intake',
                      icon: <EventAvailableIcon />,
                      onClick: (action) => setActionType(action.type),
                      permissions: ['ROLE_BASE_PERMISSION_RECRUIT_INDEX_COMPLETE_INTAKE'],
                  },
                  {
                      label: 'Resync',
                      type: 'resync',
                      icon: <AutorenewIcon />,
                      onClick: (action) => setActionType(action.type),
                      permissions: ['ROLE_BASE_PERMISSION_RECRUIT_INDEX_RESYNC_POSITION'],
                  },
                  {
                      label: 'Update Position Start Date',
                      type: 'update-start-date',
                      icon: <EditCalendarIcon />,
                      onClick: (action) => setActionType(action.type),
                      permissions: ['ROLE_BASE_PERMISSION_RECRUIT_INDEX_UPDATE_POSITION_START_DATE'],
                  },
                  {
                      label: 'Map Recruitment Policy',
                      type: 'map-recruitment-policy',
                      icon: <WorkIcon />,
                      onClick: (action) => setActionType(action.type),
                      permissions: ['ROLE_BASE_PERMISSION_RECRUIT_INDEX_MAP_RECRUITMENT_POLICY_TO_POSITIONS'],
                  },
              ];

    const filteredActionItems =
        type === 'bulk'
            ? actionItems.filter((action) =>
                  allowedPositionActions('bulk', action.type, null, { statuses: positionStatuses, user }),
              )
            : actionItems.filter((action) => allowedPositionActions('single', action.type, row, { user }));

    return (
        <Component
            style={
                ((actionType === 'create-cr' && modalType !== 1) ||
                    ['schedule-intake', 'reschedule-intake', 'complete-intake'].indexOf(actionType) >= 0) && {
                    dialog: { maxWidth: 'md' },
                }
            }
            label={label}
            onDialogClose={() => setActionType()}
            actions={filteredActionItems}
            dialog={dialogContent}
        />
    );
};

export default PositionMenu;
